import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";

// Assume these components are imported from their respective files
import HeaderModule from "./HeaderModule";
import SubheaderModule from "./SubheaderModule";

function BannerModule({ module, onChange, onDelete, isEditable }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conditionalOpen, setConditionalOpen] = useState(false);

  const handleLabelChange = (e) => {
    onChange({ ...module, label: e.target.value });
  };

  const handleColorChange = (e) => {
    onChange({ ...module, color: e.target.value });
  };

  const handleRequiredToggle = (e) => {
    onChange({ ...module, required: e.target.checked });
  };

  const handleAdminWorkflowToggle = (e) => {
    onChange({ ...module, approvalRequired: e.target.checked });
  };

  // Add Header Child
  const handleAddHeader = () => {
    const newHeader = {
      id: Date.now(), // Unique identifier; you might use a more robust method
      type: "header",
      text: "New Header",
      // Include other default properties if needed
    };
    const updatedChildren = module.children
      ? [...module.children, newHeader]
      : [newHeader];
    onChange({ ...module, children: updatedChildren });
  };

  // Add Subheader Child
  const handleAddSubheader = () => {
    const newSubheader = {
      id: Date.now() + 1, // Ensure a unique ID
      type: "subheader",
      text: "New Subheader",
      // Include other default properties if needed
    };
    const updatedChildren = module.children
      ? [...module.children, newSubheader]
      : [newSubheader];
    onChange({ ...module, children: updatedChildren });
  };

  // Optionally, a function to delete a child module based on its id
  const handleDeleteChild = (childId) => {
    const updatedChildren = module.children.filter(
      (child) => child.id !== childId,
    );
    onChange({ ...module, children: updatedChildren });
  };

  // Render child components based on type
  const renderChildModule = (child) => {
    switch (child.type) {
      case "header":
        return (
          <HeaderModule
            key={child.id}
            module={child}
            onChange={(updatedChild) => {
              // Update the specific child in the parent's children array
              const updatedChildren = module.children.map((c) =>
                c.id === child.id ? updatedChild : c,
              );
              onChange({ ...module, children: updatedChildren });
            }}
            onDelete={() => handleDeleteChild(child.id)}
            isEditable={isEditable}
          />
        );
      case "subheader":
        return (
          <SubheaderModule
            key={child.id}
            module={child}
            onChange={(updatedChild) => {
              const updatedChildren = module.children.map((c) =>
                c.id === child.id ? updatedChild : c,
              );
              onChange({ ...module, children: updatedChildren });
            }}
            onDelete={() => handleDeleteChild(child.id)}
            isEditable={isEditable}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      {/* Module Header */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          BANNER MODULE
        </Typography>
        {isEditable && (
          <Box>
            <Tooltip title="Settings">
              <IconButton
                onClick={() => setSettingsOpen(!settingsOpen)}
                size="small"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Conditional Visibility">
              <IconButton
                onClick={() => setConditionalOpen(!conditionalOpen)}
                size="small"
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Module">
              <IconButton onClick={onDelete} size="small" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Module Preview */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          padding: 2,
          backgroundColor: module.color || "#f5f5f5",
          marginBottom: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontFamily: "Roboto", color: "#000" }}>
          {module.label || "Banner"}
        </Typography>
        {module.children && module.children.length > 0 ? (
          <Box sx={{ marginTop: 2 }}>
            {module.children.map((child) => renderChildModule(child))}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No child modules added.
          </Typography>
        )}
      </Box>

      {/* Settings Panel */}
      {isEditable && settingsOpen && (
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label="Banner Label"
            value={module.label || ""}
            onChange={handleLabelChange}
            fullWidth
            margin="dense"
          />
          <TextField
            type="color"
            label="Banner Color"
            value={module.color || "#f5f5f5"}
            onChange={handleColorChange}
            margin="dense"
            fullWidth
          />

          {/* New Status Dropdowns */}
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="editable-status-label">
                  Editable when in Status:
                </InputLabel>
                <Select
                  labelId="editable-status-label"
                  value={module.editableWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, editableWhenStatus: e.target.value })
                  }
                  label="Editable when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="visible-status-label">
                  Visible when in Status:
                </InputLabel>
                <Select
                  labelId="visible-status-label"
                  value={module.visibleWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, visibleWhenStatus: e.target.value })
                  }
                  label="Visible when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ marginTop: 1 }}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.required || false}
                    onChange={handleRequiredToggle}
                    color="primary"
                  />
                }
                label="Required"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.approvalRequired || false}
                    onChange={handleAdminWorkflowToggle}
                    color="primary"
                  />
                }
                label="Admin Workflow"
              />
            </Grid>
          </Grid>

          {/* Buttons to add child modules */}
          {isEditable && (
            <Box sx={{ marginTop: 2 }}>
              <Button variant="outlined" onClick={handleAddHeader}>
                Add Header Module
              </Button>
              <Button
                variant="outlined"
                sx={{ marginLeft: 1 }}
                onClick={handleAddSubheader}
              >
                Add Subheader Module
              </Button>
            </Box>
          )}
        </Box>
      )}

      {isEditable && conditionalOpen && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2">
            Conditional Visibility settings...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default BannerModule;
